// 剣舞の役者の速い連・左右の交互打ち（開発版 ?bladeQuick=alt1 と同じ技）を模型で（2026-11-03）。
//   技の中身＝docs/research/2026-11-04-blade-quick-alternate.md：手前・奥・手前・奥の剣が交互に、肩の上から振り下ろす。
//   足はそろえてつま先、1打ごとに少し前へ滑る。合図＝頭の上で2本を交差。最後の X は無し。
//   拍は実機（src/blade_dancer_arle.ts の速い連）に合わせる＝溜め 700・当たり 120・隙間 164＋1コマ＝1打ごと 300・戻り 700。
//   溜めの中の踏み込み＝当たりの 180ms 前から（preActiveLungeMs）。tests/boss_mockup.test.ts が実機との一致を見張る。

import { HUMAN_STAND } from '../body'
import { posePatch, type MockupMove, type Pose, type Vec } from '../core'

export const BLADE_QUICK_ALT_BEAT = { chargeMs: 700, activeMs: 120, stepMs: 300, recoveryMs: 700, lungeMs: 180, hits: 4 }
const B = BLADE_QUICK_ALT_BEAT
/** 頭の前の立ち姿の長さ（輪で見せる時の構え） */
const LEAD = 500

const hp = posePatch(HUMAN_STAND)
const feet: Record<string, Vec> = { kF: [9, -37], fF: [10, -1], kB: [4, -37], fB: [4, -1] } // そろえた足・つま先
const lean: Record<string, Vec> = { hip: [4, -73], chest: [10, -111], head: [13, -130] }
const CROUCH = hp({ hip: [-1, -71], chest: [1, -110], head: [2, -130] })
const CROSS = hp({ hip: [0, -76], chest: [1, -116], head: [0, -136], eF: [14, -136], hF: [6, -162], eB: [-10, -136], hB: [-2, -162], sF: 122, sB: 58 })
const CROSS2 = hp({ hip: [-2, -77], chest: [-1, -117], head: [-2, -137], eF: [12, -140], hF: [4, -168], eB: [-12, -140], hB: [-4, -168], sF: 128, sB: 62 })
// 手前の剣を肩の上へ振りかぶる（奥の剣は下ろした後）
const windF = (rx: number): Pose => hp({ ...lean, ...feet, eF: [8, -138], hF: [-2, -150], sF: -150, eB: [14, -100], hB: [22, -84], sB: 70, rx })
// 手前の剣が前下へ（約48°）・奥の剣は頭の上に立つ
const hitF = (rx: number): Pose => hp({ ...lean, ...feet, eF: [28, -110], hF: [38, -94], sF: 48, eB: [2, -138], hB: [6, -162], sB: -95, rx })
const ftF = (rx: number): Pose => hp({ ...lean, ...feet, eF: [24, -100], hF: [30, -80], sF: 78, eB: [0, -138], hB: [2, -160], sB: -110, rx })
// 奥の剣（左右が入れ替わる）
const windB = (rx: number): Pose => hp({ ...lean, ...feet, eB: [6, -138], hB: [-4, -150], sB: -150, eF: [18, -100], hF: [26, -84], sF: 70, rx })
const hitB = (rx: number): Pose => hp({ ...lean, ...feet, eB: [24, -110], hB: [34, -94], sB: 48, eF: [6, -138], hF: [10, -162], sF: -95, rx })
const ftB = (rx: number): Pose => hp({ ...lean, ...feet, eB: [20, -100], hB: [26, -80], sB: 78, eF: [4, -138], hF: [6, -160], sF: -110, rx })
const settle = (rx: number): Pose => hp({ eF: [16, -96], hF: [22, -80], sF: 40, eB: [-2, -96], hB: [2, -80], sB: 20, rx })
const stand = (rx: number): Pose => hp({ rx })

const LUNGE = 70 // 当たりの前の踏み込み（模型の単位）
const SLIDE = 14 // 1打ごとの前滑り
const hitAt = (i: number) => LEAD + B.chargeMs + i * B.stepMs
const rxAt = (i: number) => LUNGE + i * SLIDE
const lastEnd = hitAt(B.hits - 1) + B.activeMs
const END_RX = rxAt(B.hits - 1) + 4

function strikeKeys(): { at: number; pose: Pose }[] {
  const out: { at: number; pose: Pose }[] = []
  for (let i = 0; i < B.hits; i++) {
    const front = i % 2 === 0
    out.push({ at: hitAt(i), pose: (front ? hitF : hitB)(rxAt(i)) })
    out.push({ at: hitAt(i) + B.activeMs, pose: (front ? ftF : ftB)(rxAt(i) + 4) })
    if (i < B.hits - 1) out.push({ at: hitAt(i) + B.activeMs + 82, pose: (front ? windB : windF)(rxAt(i) + 9) }) // 隙間の半ばで次の剣を振りかぶる
  }
  return out
}

export const BLADE_QUICK_ALT_MOCKUP: MockupMove = {
  id: 'blade-quick-alt', boss: '剣舞の役者', label: '前の版：腕だけで振る交互打ち（11-03 作者が直しを指示）', body: 'human-dual-dress',
  durMs: lastEnd + B.recoveryMs + 480, scale: 0.8, baseX: 310, heroAt: 150,
  keys: [
    { at: 0, pose: stand(0) }, { at: LEAD, pose: CROUCH }, { at: LEAD + 70, pose: CROSS }, { at: LEAD + 385, pose: CROSS2 },
    { at: hitAt(0) - B.lungeMs, pose: windF(0) },
    ...strikeKeys(),
    { at: lastEnd + 252, pose: settle(END_RX) }, { at: lastEnd + 609, pose: stand(END_RX) }, { at: lastEnd + B.recoveryMs + 480, pose: stand(END_RX) },
  ],
  phases: [
    { from: 0, to: LEAD, label: '構え', kind: 'rest' },
    { from: LEAD, to: hitAt(0) - B.lungeMs, label: '合図：頭の上で2本を交差', kind: 'tell' },
    { from: hitAt(0) - B.lungeMs, to: hitAt(0), label: '踏み込み', kind: 'tell' },
    { from: hitAt(0), to: lastEnd, label: '手前・奥・手前・奥（約0.3秒ごと）', kind: 'hit' },
    { from: lastEnd, to: lastEnd + B.recoveryMs + 480, label: '戻り', kind: 'rest' },
  ],
  hits: Array.from({ length: B.hits }, (_, i) => hitAt(i)), activeMs: B.activeMs,
  box: [22, 100, -150, -40], paths: ['sF', 'sB'], fadeOutMs: 300,
  source: '実機の速い連（?bladeQuick=alt1）と同じ拍：溜め 0.70秒・1打ごと 0.30秒・当たり 0.12秒・戻り 0.70秒',
}
